import { ICalendarPrintByDoctorModel } from "@/Appointments/Models/UI/ICalendarPrintByDoctorModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { Popup } from "@/Common/Components/Modals/Popup";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";

interface IPrintMDDialogProps {
    Model: ICalendarPrintByDoctorModel
}

class PrintMDDialog extends BaseReactComponent<IPrintMDDialogProps> {
    public render(): JSX.Element {
        return (
            <Popup
                manageOverlay
                cssClass="mds"
                title={cacheStore.GetString("PrintAppointmentTitleLabel")}
                closeTarget={LocalOperations.Calendar.Calendar_PrintByDoctorDialog_Cancel_Click}
            >
                {this.props.Model.get("Sites").size > 1
                    ? this.multiSitePopup()
                    : this.singleSitePopup()
                }
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => this.onCancelClick()}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={() => this.onPrintClick()}
                            label={cacheStore.GetString("PrintLabel")}
                        />
                    </ButtonGroup>
                </div>
            </Popup>
        );
    }

    private singleSitePopup(): JSX.Element {
        return (
            <div className="p-body">
                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <CheckboxControl
                            id="employees_check"
                            label={cacheStore.GetString("EmployeesLabel").toUpperCase()}
                            operation={LocalOperations.Calendar.Checkbox_Change}
                            checked={this.props.Model.get("IsAllEmployeesChecked")}
                            target={CheckboxTarget.Myle_Calendar_PrintByDoctor_SearchEmployees}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12 scrollableSection">
                        <div style={{ width: "250px", padding: "0 0 6px" }}>
                            <input
                                className="form-control"
                                type="text"
                                value={this.props.Model.get("EmployeesSearchText")}
                                onChange={e => this.onEmployeeFilterChange(e)}
                                placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                            />
                        </div>
                        <div className="scroll-limiter">
                            <CheckedListControl
                                idModifier="printMd_"
                                DataSource={this.props.Model.get("EmployeesFiltered")}
                                Operation={LocalOperations.Calendar.Checkbox_Change}
                                limitTextWidth
                                sort
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <div className="inline-radios clearfix">
                            <RadioButton
                                items={Immutable.List([
                                    new KeyValuePair(cacheStore.GetString("DateLabel"), "Date"),
                                    new KeyValuePair(cacheStore.GetString("IdentifierDLabel"), "Identifier"),
                                    new KeyValuePair(cacheStore.GetString("PatientLabel"), "Patient"),
                                    new KeyValuePair(cacheStore.GetString("LabelLabel") + " C1", "Labels"),
                                    new KeyValuePair(cacheStore.GetString("BorrowingSheetLabel"), "BorrowingSheet")
                                ])}
                                radioInputGroupId="ClinicianPrintOptions"
                                onePerLine={false}
                                changeOperation={LocalOperations.Calendar.Calendar_PrintByDoctorDialog_ClinicianPrintOptionChange}
                                selectedItem={this.props.Model.get("SelectedPrintOption")}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private multiSitePopup(): JSX.Element {
        return (
            <div className="p-body">
                <div className="row">
                    <div className="col-lg-4 col-md-4 col-sm-4">
                        <CheckboxControl
                            id="sites_check"
                            label={cacheStore.GetString("SitesLabel").toUpperCase()}
                            operation={LocalOperations.Calendar.Checkbox_Change}
                            checked={this.props.Model.get("IsAllSitesChecked")}
                            target={CheckboxTarget.Myle_Calendar_PrintByDoctor_SearchSites}
                        />
                    </div>
                    <div className="col-lg-8 col-md-8 col-sm-8">
                        <CheckboxControl
                            id="employees_check"
                            label={cacheStore.GetString("EmployeesLabel").toUpperCase()}
                            operation={LocalOperations.Calendar.Checkbox_Change}
                            checked={this.props.Model.get("IsAllEmployeesChecked")}
                            target={CheckboxTarget.Myle_Calendar_PrintByDoctor_SearchEmployees}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-4 col-md-4 col-sm-4">
                        <CheckedListControl
                            DataSource={this.props.Model.get("Sites")}
                            Operation={LocalOperations.Calendar.Checkbox_Change}
                            MaxTextLength={24}
                        />
                    </div>
                    <div className="col-lg-8 col-md-8 col-sm-8 scrollableSection">
                        <div style={{ width: "250px", padding: "0 0 6px" }}>
                            <input
                                className="form-control"
                                type="text"
                                value={this.props.Model.get("EmployeesSearchText")}
                                onChange={e => this.onEmployeeFilterChange(e)}
                                placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                            />
                        </div>
                        <div className="scroll-limiter">
                            <CheckedListControl
                                DataSource={this.props.Model.get("EmployeesFiltered")}
                                Operation={LocalOperations.Calendar.Checkbox_Change}
                                limitTextWidth
                                sort
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <div className="inline-radios clearfix">
                            <RadioButton
                                items={Immutable.List([
                                    new KeyValuePair(cacheStore.GetString("DateLabel"), "Date"),
                                    new KeyValuePair(cacheStore.GetString("IdentifierDLabel"), "Identifier"),
                                    new KeyValuePair(cacheStore.GetString("PatientLabel"), "Patient"),
                                    new KeyValuePair(cacheStore.GetString("LabelLabel") + " C1", "Labels"),
                                    new KeyValuePair(cacheStore.GetString("BorrowingSheetLabel"), "BorrowingSheet")
                                ])}
                                radioInputGroupId="ClinicianPrintOptions"
                                onePerLine={false}
                                changeOperation={LocalOperations.Calendar.Calendar_PrintByDoctorDialog_ClinicianPrintOptionChange}
                                selectedItem={this.props.Model.get("SelectedPrintOption")}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private onCancelClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.Calendar_PrintByDoctorDialog_Cancel_Click);
    }

    private onPrintClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.Calendar_PrintByDoctorDialog_Print_Click);
    }

    private onEmployeeFilterChange(event: React.ChangeEvent<HTMLInputElement>): void {
        sessionStore.dispatch(LocalOperations.Calendar.Calendar_PrintByDoctorDialog_EmployeeFilterChange, { value: event.target.value });
    }
}

export { PrintMDDialog };
